import { useMemo, useState } from "react";
import { IconMessageCircle } from "@tabler/icons-react";
import type { PricelistItemDto, ProductDetailsDto, ProductVariantDto } from "../../../../api";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Heading, Text } from "../../../../components/common/typography";
import { useWishlistStore } from "../../domain/stores/useWishlistStore";
import { ProductImageGallery } from "./product-image-gallery";
import {
  ProductAttributeGroups,
  buildDisplayGroups,
  getVariantAxisValues,
  withPriceLabels,
} from "./product-attribute-groups";
import { ProductPricingPanel } from "./product-pricing-panel";
import { ProductDescriptionSection } from "./product-description-section";
import { ProductSpecsSection } from "./product-specs-section";
import { useActiveLoyaltyPromotions } from "../../../loyalty/hooks/useLoyaltyPromotions";
import { getActivePromotionForProduct } from "../../../loyalty/utils/resolve-effective-multiplier";
import { LoyaltyPromoBanner } from "../../../loyalty/presentation/components/loyalty-promo-banner";

type TemplateProduct = ProductDetailsDto["product"];

interface BuyerProductDetailsViewProps {
  baseProduct: TemplateProduct;
  variants: ProductVariantDto[];
  selectedVariant: ProductVariantDto;
  onSelectVariant: (variantId: number) => void;
  quantity: number;
  onQuantityChange: (qty: number) => void;
  onBuyNow?: () => void;
  onAddToBasket?: () => void;
  onRequestQuotation?: () => void;
  isRequestingQuotation?: boolean;
  onToggleWishlist?: () => void;
  isInWishlist?: boolean;
  onChatAbout?: () => void;
  onAskAboutDelivery?: () => void;
}

function useActiveTier(tiers: PricelistItemDto[] | null | undefined, quantity: number) {
  return useMemo(() => {
    if (!tiers || tiers.length === 0) return null;
    const sorted = [...tiers].sort((a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0));
    let match = sorted[0];
    for (const tier of sorted) {
      if (quantity >= (tier.minQuantity ?? 1)) match = tier;
      else break;
    }
    return match;
  }, [tiers, quantity]);
}

export function BuyerProductDetailsView({
  baseProduct,
  variants,
  selectedVariant,
  onSelectVariant,
  quantity,
  onQuantityChange,
  onBuyNow,
  onAddToBasket,
  onRequestQuotation,
  isRequestingQuotation,
  onToggleWishlist,
  isInWishlist,
  onChatAbout,
  onAskAboutDelivery,
}: BuyerProductDetailsViewProps) {
  const variantId = selectedVariant?.id;
  const wishlistVariantIds = useWishlistStore((s) => s.wishlistVariantIds);
  const storeToggle = useWishlistStore((s) => s.toggle);
  const effectiveIsInWishlist = isInWishlist ?? (variantId != null && wishlistVariantIds.includes(variantId));
  const handleToggleWishlist = onToggleWishlist ?? (() => { if (variantId != null) void storeToggle(variantId); });

  const [expandDescription, setExpandDescription] = useState(false);
  const [thumbnailImage, setThumbnailImage] = useState<string | null>(null);

  const currentAxisValues = useMemo(
    () => (selectedVariant ? getVariantAxisValues(selectedVariant) : {}),
    [selectedVariant],
  );

  const displayGroups = useMemo(() => {
    const groups = buildDisplayGroups(variants);
    return withPriceLabels(groups, variants, currentAxisValues);
  }, [variants, currentAxisValues]);

  const selectorAxisKeys = useMemo(
    () => new Set(displayGroups.map((g) => g.key)),
    [displayGroups],
  );

  const handleAttributeSelect = (axisKey: string, value: string) => {
    setThumbnailImage(null);
    const target = { ...currentAxisValues, [axisKey]: value };
    const exact = variants.find((v) => {
      const a = getVariantAxisValues(v);
      return Object.entries(target).every(([k, val]) => a[k] === val);
    });
    if (exact) return onSelectVariant(exact.id);
    const fallback = variants.find((v) => getVariantAxisValues(v)[axisKey] === value);
    if (fallback) onSelectVariant(fallback.id);
  };

  const name = baseProduct?.name || selectedVariant?.name || "—";
  const description = baseProduct?.description || selectedVariant?.description;
  const stock = Number(selectedVariant?.supplierStock ?? 0);
  const sku = selectedVariant?.defaultCode ?? baseProduct?.defaultCode;
  const displayImage = thumbnailImage || selectedVariant?.image || baseProduct?.image;

  const baseTiers = (baseProduct as unknown as { pricelistItems?: PricelistItemDto[] }).pricelistItems;
  const tiers = (selectedVariant?.pricelistItems?.length
    ? selectedVariant.pricelistItems
    : baseTiers) as PricelistItemDto[] | undefined;

  const sortedTiers = useMemo(
    () => tiers ? [...tiers].sort((a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0)) : [],
    [tiers],
  );

  const activeTier = useActiveTier(sortedTiers, quantity);

  const currentPrice = useMemo(() => (
    activeTier?.fixedPrice ??
    (selectedVariant?.listPrice ?? baseProduct?.listPrice ?? 0) + (selectedVariant?.priceExtra ?? 0)
  ), [activeTier, baseProduct, selectedVariant]);

  const variantDiscount = selectedVariant?.discountPercentage ?? 0;
  const strikePrice = variantDiscount > 0
    ? (selectedVariant?.standardPrice ?? baseProduct?.standardPrice ?? null)
    : null;
  const thumbnailVariants = variants.slice(0, 4);
  const padCount = Math.max(0, 4 - thumbnailVariants.length);

  const { data: activePromotions } = useActiveLoyaltyPromotions();
  const promoTemplateId = selectedVariant?.productTemplateId ?? baseProduct?.id;
  const activePromotion =
    promoTemplateId != null ? getActivePromotionForProduct(promoTemplateId, activePromotions) : null;

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-3">
        <ProductImageGallery
          displayImage={displayImage}
          name={name}
          galleryImages={(baseProduct as unknown as { galleryImages?: string[] })?.galleryImages ?? []}
          thumbnailVariants={thumbnailVariants}
          padCount={padCount}
          isInWishlist={effectiveIsInWishlist}
          onToggleWishlist={handleToggleWishlist}
          onSelectVariant={onSelectVariant}
          onThumbnailClick={setThumbnailImage}
        />

        <div className="flex min-w-0 flex-col gap-5">
          <div className="flex items-start gap-3">
            <Heading variant="h2" tone="dark" className="min-w-0">{name}</Heading>
            {variantDiscount > 0 && <Chip variant="secondary" className="shrink-0 mt-1">{variantDiscount}% off</Chip>}
          </div>
          <div className="flex items-center gap-2">
            {sku ? <Text variant="bodySm" tone="muted">{sku}</Text> : null}
            {sku ? <Text variant="bodySm" tone="muted">·</Text> : null}
            <Text variant="bodySmBold" tone="dark" className="text-accentGreenDark">In stock: {stock} units</Text>
          </div>
          <ProductAttributeGroups
            groups={displayGroups}
            current={currentAxisValues}
            onSelect={handleAttributeSelect}
          />
          <ProductSpecsSection
            variant={selectedVariant}
            template={baseProduct}
            excludeKeys={selectorAxisKeys}
          />
        </div>

        <div className="flex flex-col gap-3">
          {activePromotion && <LoyaltyPromoBanner promotion={activePromotion} />}
          <ProductPricingPanel
            currentPrice={currentPrice}
            strikePrice={strikePrice}
            discountPercentage={variantDiscount}
            sortedTiers={sortedTiers}
            activeTier={activeTier}
            quantity={quantity}
            onQuantityChange={onQuantityChange}
            onBuyNow={onBuyNow}
            onAddToBasket={onAddToBasket}
            onRequestQuotation={onRequestQuotation}
            isRequestingQuotation={isRequestingQuotation}
            displayImage={displayImage}
            leadTimeDays={baseProduct?.leadTimeDays}
            onAskAboutDelivery={onAskAboutDelivery}
          />
          <Button
            variant="secondary"
            fullWidth
            leftIcon={<IconMessageCircle size={16} />}
            onClick={onChatAbout}
          >
            Chat about this product
          </Button>
        </div>
      </div>

      <ProductDescriptionSection
        description={description}
        expanded={expandDescription}
        onToggle={() => setExpandDescription((v) => !v)}
      />
    </div>
  );
}

export default BuyerProductDetailsView;
